<template>
  <component :is="componentVariant" :class="[...styles, 'text-color']">
    <slot></slot>
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Component } from 'vue'
import type { TypographyVariantsType } from '@/ui-library/types/ui'

const props = defineProps<{
  variant: TypographyVariantsType
  color?: string
  fontStyle?: string
  fontWeight?: number
  textAlign?: string
  textDecoration?: string
  textTransform?: string
  textTruncate?: boolean
  whiteSpace?: string
  is?: string | Component
}>()

const TypographyComponentEnum = {
  hero: 'h1',
  'headline-large': 'h1',
  'headline-medium': 'h2',
  'headline-small': 'h3',
  'title-large': 'h4',
  'title-medium': 'h5',
  'title-small': 'h6',
  'label-large': 'p',
  'label-medium': 'p',
  'label-small': 'p',
  'body-large': 'p',
  'body-medium': 'p',
  'body-small': 'span',
}

const styles = computed(() => {
  const classNames = {
    [props.variant]: true,
    ...(props.fontStyle && { ['font-style']: true }),
    ...(props.fontWeight && { ['font-weight']: true }),
    ...(props.textAlign && { ['text-align']: true }),
    ...(props.textDecoration && { ['text-decoration']: true }),
    ...(props.textTransform && { ['text-transform']: true }),
    ...(props.textTruncate && { ['text-truncate']: true }),
    ...(props.whiteSpace && { ['whitespace']: true }),
  }
  return Object.keys(classNames)
})
const componentVariant = computed(() => props.is || TypographyComponentEnum[props.variant] || 'div')
const textColor = computed(() => props.color || '#031e51')
</script>

<style scoped>
.hero {
  font-size: 48px;
  line-height: 56px;
  font-weight: 600;
}

.headline-large {
  font-size: 32px;
  line-height: 40px;
  font-weight: 600;
}

.headline-medium {
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
}

.headline-small {
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
}

.title-large {
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
}

.title-medium {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.label-large {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}

.label-medium {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
}

.label-small {
  font-size: 10px;
  line-height: 16px;
  font-weight: 500;
}

.body-large {
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
}

.body-medium {
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
}

.body-small {
  font-size: 12px;
  line-height: 16px;
  font-weight: 400;
}

.text-align {
  text-align: v-bind(textAlign);
}

.font-weight {
  font-weight: v-bind(fontWeight);
}

.font-style {
  font-style: v-bind(fontStyle);
}

.text-decoration {
  text-decoration: v-bind(textDecoration);
}

.text-transform {
  text-transform: v-bind(textTransform);
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.text-color {
  color: v-bind(textColor);
}
</style>
